import React from 'react';

import { Tooltip } from 'antd';

import styled from 'styled-components';

import { getBookCover } from '@libs/NwUtils';

import CreatedBy from '@components/CreatedBy/CreatedBy';
import AvatarBg from '@components/Gui/AvatarBg';
import NwIcon from '@components/Gui/NwIcon';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const BookRow = styled.div`
    display: flex;
    gap: 0.75rem;
    padding: 0.75rem 0;
    align-items: flex-start;
    border-bottom: 1px solid ${(props) => props.theme.BorderColor};

    &:last-child {
        border-bottom: none;
    }
`;

const TextBlock = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
`;

const BookTitle = styled.div`
    font-weight: 700;
    font-size: 0.85rem;
    line-height: 1.25;
    color: ${(props) => props.theme.TextColor};
    text-transform: uppercase;
`;

const CreatedByPopoverWrap = styled.div`
    font-size: ${(props) => props.theme.FontSizeSm};
    color: ${(props) => props.theme.TextColorMuted};
`;

const FastPackageOrderLine = styled.div`
    font-size: ${(props) => props.theme.FontSizeSm};
    color: ${(props) => props.theme.TextColorMuted};
`;

/* AvatarBg non inoltra il ref: il Tooltip ha bisogno di un nodo suo su cui agganciarsi. */
const CoverWrap = styled.span`
    display: inline-flex;
    flex: none;
`;

const CoverTooltipTitle = styled.div`
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    margin-bottom: 0.15rem;
`;

const CoverTooltipMeta = styled.div`
    font-size: ${(props) => props.theme.FontSizeSm};
    opacity: 0.85;
`;

const EmptyCover = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${(props) => props.theme.BorderColor};
    width: ${(props) => props.$size}px;
    height: ${(props) => props.$size}px;
    flex-shrink: 0;
    font-size: 1.4rem;
`;

const isEmptyBook = (book) => {
    if (typeof book.DocumentPlaylistsCount !== 'undefined') {
        return book.DocumentPlaylistsCount === 0;
    }
    if (typeof book.DocumentPlaylists !== 'undefined') {
        return book.DocumentPlaylists.length === 0;
    }
    return false;
};

/**
 * La copertina del book, o il segnaposto quando il book e' vuoto. withTooltip solo dove
 * la copertina sta da sola: accanto alle righe le stesse informazioni sono gia' scritte.
 */
export const ModelBookPreviewCover = ({ book, size, withTooltip }) => {
    const cover = isEmptyBook(book) ? null : getBookCover(book);
    const picture = cover ? (
        <CoverWrap>
            <AvatarBg size={size} src={cover} shape="square" />
        </CoverWrap>
    ) : (
        <EmptyCover $size={size}>
            <NwIcon icon={byPrefixAndName.fasl['empty-set']} />
        </EmptyCover>
    );

    if (!withTooltip) {
        return picture;
    }

    return (
        <Tooltip
            mouseEnterDelay={0.4}
            // Il popover che lo contiene sta a 9999: senza questo il tooltip gli finisce dietro.
            zIndex={10000}
            title={
                <>
                    <CoverTooltipTitle>{book.Name}</CoverTooltipTitle>
                    <CoverTooltipMeta>
                        <CreatedBy object={book} textonly emptyText="no author" />
                    </CoverTooltipMeta>
                </>
            }
        >
            {picture}
        </Tooltip>
    );
};

const ModelBooksPreviewRow = ({ book, size, showOrder }) => {
    const hasOrder =
        showOrder &&
        book.FastPackageOrder != null &&
        book.FastPackageOrder !== '' &&
        Number.isFinite(Number(book.FastPackageOrder));

    return (
        <BookRow>
            <ModelBookPreviewCover book={book} size={size} />
            <TextBlock>
                <BookTitle>{book.Name}</BookTitle>
                <CreatedByPopoverWrap>
                    <CreatedBy object={book} short textonly textTransform="capitalize" />
                </CreatedByPopoverWrap>
                {hasOrder && <FastPackageOrderLine>{`(${Number(book.FastPackageOrder) + 1})`}</FastPackageOrderLine>}
            </TextBlock>
        </BookRow>
    );
};

/** 30% piu' piccola della cover da 96px usata altrove. */
export const PREVIEW_COVER_SIZE = Math.round(96 * 0.7);

const ModelBooksPreviewRows = ({ books, size = PREVIEW_COVER_SIZE, showOrder = true }) =>
    books.map((book) => <ModelBooksPreviewRow key={book.ID} book={book} size={size} showOrder={showOrder} />);

export default ModelBooksPreviewRows;
